import { PlusIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import userJobApi from "@/api/userJob";
import { Button } from "@/components/Button/Button";
import { Modal } from "@/components/Modal/Modal";
import { ModalFooter } from "@/components/Modal/ModalFooter";
import ResumePreviewGrid from "@/components/ResumePreviewGrid";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useInvalidateUserJobsCache } from "@/hooks/useAppData";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { closeModal, openModal } from "@/store/modal/slice";
import { getResumes } from "@/store/resume/selectors";
import { ModalTypes } from "@/types/modal";
import { Resume } from "@/types/resume";
import { IUserJob } from "@/types/userJob";

interface JobResumesModalProps {
    activeJob: IUserJob;
}

const sortResumes = (resumes: Resume[]) => {
    return [...resumes].sort((a, b) => {
        const aDate = new Date(a.updatedAt);
        const bDate = new Date(b.updatedAt);
        return bDate.getTime() - aDate.getTime(); // Most recent first
    });
};

const JobResumesModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.USER_JOB_RESUMES));
    const modalProps = useAppSelector(
        getModalProps(ModalTypes.USER_JOB_RESUMES),
    ) as JobResumesModalProps;
    const resumes = useAppSelector(getResumes);
    const dispatch = useAppDispatch();
    const invalidateUserJobs = useInvalidateUserJobsCache();

    const initiallyLinkedResumeIds = useRef<string[]>([]);
    const activeJobTitle = useRef("");

    useEffect(() => {
        if (modalProps?.activeJob?.linkedResumeIds) {
            initiallyLinkedResumeIds.current =
                modalProps.activeJob.linkedResumeIds;
        }
        if (modalProps?.activeJob?.jobTitle) {
            activeJobTitle.current = modalProps.activeJob.jobTitle;
        }
    }, [modalProps?.activeJob?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const [selectedResumeIds, setSelectedResumeIds] = useState<string[]>(
        initiallyLinkedResumeIds.current,
    );

    const handleSaveChanges = async () => {
        await userJobApi.updateUserJobLinkedResumes(modalProps?.activeJob?.id, {
            linkedResumeIds: selectedResumeIds,
        });
        invalidateUserJobs();

        dispatch(closeModal(ModalTypes.USER_JOB_RESUMES));
    };

    const sortedLinkedResumes = useMemo(() => {
        const linkedResumes = resumes.filter(resume =>
            initiallyLinkedResumeIds.current.includes(resume.id),
        );
        return sortResumes(linkedResumes);
    }, [resumes, initiallyLinkedResumeIds.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const sortedUnlinkedResumes = useMemo(() => {
        const unlinkedResumes = resumes.filter(
            resume => !initiallyLinkedResumeIds.current.includes(resume.id),
        );
        return sortResumes(unlinkedResumes);
    }, [resumes, initiallyLinkedResumeIds.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const sortedResumes = useMemo(() => {
        return [...sortedLinkedResumes, ...sortedUnlinkedResumes];
    }, [sortedLinkedResumes, sortedUnlinkedResumes]);

    useEffect(() => {
        setSelectedResumeIds(initiallyLinkedResumeIds.current);
    }, [modalProps?.activeJob?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Modal
            modalType={ModalTypes.USER_JOB_RESUMES}
            open={isOpen}
            title={`Attached Resumes for ${activeJobTitle.current}`}
            width="4xl"
            afterClose={() => {
                setSelectedResumeIds([]);
            }}
            footer={
                resumes?.length > 0 ? (
                    <ModalFooter
                        primaryActionText="Save"
                        primaryActionOnClick={handleSaveChanges}
                    />
                ) : null
            }
        >
            {resumes?.length > 0 ? (
                <div className="flex min-h-[200px] flex-col gap-4">
                    <AppText
                        variant="regular"
                        className="text-neutral-700"
                    >
                        Choose resumes to attach to this job
                    </AppText>

                    <div className="flex h-full max-h-[100%] flex-col lg:h-[60vh] lg:max-h-[500px]">
                        <div className="no-scrollbar h-full overflow-y-auto px-4">
                            <ResumePreviewGrid
                                resumes={sortedResumes}
                                selectedResumeIds={selectedResumeIds}
                                setSelectedResumeIds={setSelectedResumeIds}
                                multipleSelect={true}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex min-h-[200px] flex-col items-center justify-center">
                    <AppText
                        variant="contextheading"
                        className="mb-2 text-neutral-900"
                    >
                        Create your first resume
                    </AppText>
                    <AppText
                        variant="regular"
                        className="mb-4 w-[300px] text-center text-neutral-700 lg:w-[500px]"
                    >
                        Create your first resume to attach it to a job and start
                        tracking it’s progress, get tailored improvements and
                        more
                    </AppText>
                    <Button
                        color="primary"
                        onClick={() => {
                            dispatch(
                                openModal({
                                    modalType: ModalTypes.NEW_RESUME,
                                }),
                            );
                            dispatch(closeModal(ModalTypes.USER_JOB_RESUMES));
                        }}
                        leftIcon={<PlusIcon size={16} />}
                    >
                        New Resume
                    </Button>
                </div>
            )}
        </Modal>
    );
};

export default JobResumesModal;
